/* web/src/style.css — design tokens + global resets per UI-SPEC */

:root {
  /* Spacing scale (8-pt) */
  --space-xs:  4px;
  --space-sm:  8px;
  --space-md: 16px;
  --space-lg: 24px;
  --space-xl: 32px;
  --space-2xl: 48px;
  --space-3xl: 64px;

  /* Typography stacks */
  --font-display: ui-rounded, "SF Pro Rounded", "Segoe UI", system-ui, sans-serif;
  --font-body:    system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
  --font-fancy:   "Caveat Brush", ui-rounded, "SF Pro Rounded", system-ui, sans-serif;

  /* Type sizes */
  --type-body: 16px;
  --type-heading: 28px;
  --type-display: 96px;

  /* Chrome */
  --bg:           #FFFFFF;
  --surface:      #F5F5F7;
  --danger:       #D32F2F;
  --text-primary: #1A1A1A;
  --text-on-color: #FFFFFF;
  --text-muted:   #6B6B6B;

  /* Theme color slot — per-game class swap on body */
  --theme-color: #5E3FBE;   /* default = vocab purple */

  --accent-green: #2E7D32;     /* Phase 5: Game 4 first-letter color (spec PDF + UI-SPEC) */
}

/* Per-game theme overrides (UI-SPEC §"Authoritative Theme Colors") */
.theme-vocab            { --theme-color: #5E3FBE; }
.theme-word_recognition { --theme-color: #B43FA8; }
.theme-first_sound      { --theme-color: #B43FA8; }
.theme-reading          { --theme-color: #3F8E96; }
.theme-letter_sound     { --theme-color: #0F5C7A; }
.theme-missing_letter   { --theme-color: #D72E2E; }

/* Resets + global UX */
*, *::before, *::after { box-sizing: border-box; }
html, body {
  margin: 0;
  padding: 0;
  height: 100%;
  background: var(--bg);
  color: var(--text-primary);
  font-family: var(--font-body);
  font-size: var(--type-body);
  line-height: 1.5;
}
button, input { font-family: inherit; font-size: inherit; }

/* Tap target floor (UI-11) — kid app only. The admin shell adds `admin-ui`
   to <body>; admin uses native control sizing (its buttons set their own
   padding/font-size) so the 64px floor must not leak in there. */
body:not(.admin-ui) button,
body:not(.admin-ui) [role="button"] {
  min-height: 64px;
  min-width: 64px;
  user-select: none;
  -webkit-touch-callout: none;
  -webkit-user-select: none;
}

/* Reduced-motion (UI-SPEC §Accessibility Contract) */
@media (prefers-reduced-motion: reduce) {
  *, *::before, *::after {
    animation-duration: 100ms !important;
    transition-duration: 100ms !important;
  }
  /* Don't turn the infinite mic pulse into a frantic 100ms flicker — kill it. */
  .mic-btn.mic-listening { animation: none !important; }
  .mic-btn.mic-listening::before { display: none; }
  /* Keep the processing sparkles visible (still a "magic is happening" cue) but
     hold them steady instead of twinkling. */
  .mic-sparkles .spark { animation: none !important; opacity: 0.9; }
}

/* Mic "listening" pulse — a sonar ring expands out of the button in the game's
   theme color while it breathes gently, signalling the mic is live. Toggled via
   the .mic-listening class in screens/card.ts (setMicListening/setMicOff). The
   ::before ring sits behind the button so it appears to emanate from under it. */
.mic-btn.mic-listening {
  position: relative;
  animation: mic-breathe 1.6s ease-in-out infinite;
}
.mic-btn.mic-listening::before {
  content: "";
  position: absolute;
  inset: -3px; /* line up with the button's 3px border */
  border-radius: 50%;
  background: var(--theme-color);
  z-index: -1;
  pointer-events: none;
  animation: mic-sonar 1.6s ease-out infinite;
}
@keyframes mic-breathe {
  0%, 100% { transform: scale(1); }
  50%      { transform: scale(1.06); }
}
@keyframes mic-sonar {
  0%   { transform: scale(1);    opacity: 0.55; }
  100% { transform: scale(1.45); opacity: 0;    }
}

/* Mic "processing" sparkles — while the child's reply is being evaluated
   (STT→LLM→TTS) the mic greys out. Little stars twinkle over it so the wait
   reads as "magic is happening" rather than "nothing's working". The sparkle
   layer is a SIBLING of the button (not a child) so the button's grayscale
   filter doesn't dull the stars. Toggled via the .is-active class in
   screens/card.ts (sendTurnRequest). The 96px box matches the mic button. */
.mic-sparkles {
  display: none;
  position: absolute;
  top: 0;
  left: 0;
  width: 96px;
  height: 96px;
  pointer-events: none;
  z-index: 1;
}
.mic-sparkles.is-active { display: block; }
/* Each spark is a CSS four-point "twinkle" (concave star clip-path) filled with
   the game's theme color — emoji can't be tinted, so we draw the shape ourselves.
   The drop-shadow gives a soft same-color glow so it reads as a sparkle, not a
   flat diamond. */
.mic-sparkles .spark {
  position: absolute;
  background: var(--theme-color);
  clip-path: polygon(50% 0%, 60% 40%, 100% 50%, 60% 60%, 50% 100%, 40% 60%, 0% 50%, 40% 40%);
  filter: drop-shadow(0 0 3px var(--theme-color));
  opacity: 0;
  will-change: transform, opacity;
  animation: mic-sparkle 1.8s ease-in-out infinite;
}
.mic-sparkles .spark-1 { top: 2px;    left: 12px;  width: 36px; height: 36px; animation-delay: 0s;   }
.mic-sparkles .spark-2 { top: 18px;   right: 4px;  width: 26px; height: 26px; animation-delay: 0.36s; }
.mic-sparkles .spark-3 { bottom: 6px;  left: 6px;   width: 30px; height: 30px; animation-delay: 0.72s; }
.mic-sparkles .spark-4 { bottom: 16px; right: 12px; width: 32px; height: 32px; animation-delay: 1.08s; }
.mic-sparkles .spark-5 { top: 38px;   left: 40px;  width: 22px; height: 22px; animation-delay: 1.44s; }
/* Snappy twinkle: pop in fast, pop out fast, then rest — rather than a slow
   sinusoidal glow. Each spark is visible for only ~60% of its cycle. */
@keyframes mic-sparkle {
  0%   { opacity: 0; transform: scale(0.2) rotate(-45deg); }
  30%  { opacity: 1; transform: scale(1)   rotate(0deg);   }
  60%  { opacity: 0; transform: scale(0.3) rotate(45deg);  }
  100% { opacity: 0; transform: scale(0.2) rotate(-45deg); }
}

/* Picker (UI-02) — faded echo of the login background + frosted panel.
   Login says "welcome to the magical world"; the picker should say "you are
   inside the magical world, now choose an activity" — quiet atmosphere so
   the colorful game bars carry the screen. The blue-sky-mountains.png is
   the same image used full-strength on /login (screens/login.ts).
   isolation: isolate traps the negative-z-index pseudos inside the section
   so they sit beneath the bars without leaking to the document root. */
.screen-picker {
  position: relative;
  isolation: isolate;
  background:
    radial-gradient(circle at top, rgba(100, 80, 210, 0.08), transparent 35%),
    linear-gradient(180deg, #f7f8ff, #f4fbff);
}
.screen-picker::before {
  /* URL is injected as --picker-bg via inline style on .screen-picker
     (picker.ts) so esbuild doesn't try to resolve the asset at bundle
     time — /storage/* is served by the Go shell, not bundled.
     Image opacity (0.55) and overlay opacity (0.70) compound: only
     ~16% of the final pixel is image, the rest is white + page gradient.
     Pushing image opacity below ~0.40 with overlay >=0.75 makes the
     background invisible. */
  content: "";
  position: absolute;
  inset: 0;
  background-image: var(--picker-bg);
  background-size: cover;
  background-position: center;
  filter: blur(28px) saturate(0.55);
  opacity: 0.55;
  z-index: -2;
}
.screen-picker::after {
  content: "";
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.70);
  z-index: -1;
}
.game-list-panel {
  padding: var(--space-xl);
  border-radius: 32px;
  background: rgba(255, 255, 255, 0.55);
  box-shadow: 0 20px 60px rgba(70, 75, 120, 0.10);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  max-width: 100%;
}
